import React from "react";
import axios from "axios";
import { DollarSign } from "lucide-react";

const fmtDate = (d) => `${d.slice(4, 6)}/${d.slice(6, 8)}`;
const MKT = { moneyline: "Moneyline", puckline: "Puck line", total: "Totals" };
const ROWS = [
  ["official", "Official · APPROVED"], ["official:moneyline", "Official · Moneyline"], ["official:puckline", "Official · Puck line"], ["official:total", "Official · Totals"],
  ["best_bet_official", "Official best bet per game"],
  ["all", "All evaluated (hypothetical)"], ["best_bet", "Best bet per game (original rule, hypothetical)"],
  ["market:moneyline", "Moneyline (all)"], ["market:puckline", "Puck line (all)"], ["market:total", "Totals (all)"],
  ["status:VALUE", "VALUE (hypothetical)"], ["status:WATCH", "WATCH (hypothetical)"], ["status:PASS", "PASS (hypothetical)"],
  ["audited:APPROVED", "Audited v2.5 · would be APPROVED"], ["audited:VALUE", "Audited v2.5 · VALUE"], ["audited:PASS", "Audited v2.5 · PASS"],
];
const RES = { WIN: "text-emerald-300", LOSS: "text-rose-400", PUSH: "text-slate-400" };
const signed = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v}`);
const odds = (v) => (v == null ? "—" : v > 0 ? `+${v}` : `${v}`);
const BT_ROWS = [["dim|status APPROVED", "APPROVED (official)"], ["dim|status VALUE", "VALUE"], ["dim|status WATCH", "WATCH"],
  ["moneyline|APPROVED", "ML official"], ["puckline|APPROVED", "PL official"], ["total|APPROVED", "Totals official"], ["best_bet", "Best bet"],
  ["moneyline|all", "ML all"], ["puckline|all", "PL all"], ["total|all", "Totals all"]];
const TAGS = [["v2", "v2.4 (old)"], ["v21", "v2.5 (new)"]];

const Backtest = () => {
  const [d, setD] = React.useState(null);
  React.useEffect(() => { axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/game-lines/backtest`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, []);
  if (!d) return <p className="font-mono text-[10px] text-slate-600">Loading backtest…</p>;
  if (d.error || !d.games) return <p className="font-mono text-[10px] text-slate-600" data-testid="lines-backtest-pending">{d.note || "Backtest unavailable."}</p>;
  const cell = (tag, sp, k) => { const r = d[tag]?.[`${sp}|${k}`]; return r?.n ? `${r.n} · ${r.w}-${r.l} · ${signed(r.units)}u · ${signed(r.roi)}%` : "0 bets"; };
  const br = (mk) => { const o = d.v2?.[`oos|${mk}`]; const r = d.v21?.[`oos|${mk}`]; return r ? `${mk}: old ${o?.brier_model ?? "—"}/${o?.ll_model ?? "—"} → new ${r.brier_model}/${r.ll_model} (mkt ${r.brier_mkt}/${r.ll_mkt})` : "—"; };
  const clv = (tag, sp) => ["APPROVED", "VALUE"].map((st) => { const c = d[`${tag}_clv_open`]?.[`${sp}|${st}`]; return c ? `${st[0]} ${signed(c.avg_clv)} (${c.n})` : `${st[0]} —`; }).join(" · ");
  return (
    <div className="mt-3" data-testid="lines-backtest">
      <p className="mb-1 font-mono text-[9px] uppercase tracking-widest text-slate-500">Chronological backtest · 2025-26 · {d.games} games · priced at {d.price}</p>
      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="w-full font-mono text-[10px]">
          <thead className="bg-slate-900 text-[9px] uppercase tracking-wider text-slate-500"><tr>
            <th className="px-2 py-1.5 text-left">Group</th>{["validation", "oos"].flatMap((sp) => TAGS.map(([t, l]) => <th key={sp + t} className="px-2 py-1.5 text-right">{sp === "oos" ? "Out-of-sample" : "Validation"} · {l}</th>))}
          </tr></thead>
          <tbody>
            {BT_ROWS.map(([k, l]) => (
              <tr key={k} className="border-t border-slate-800/60" data-testid={`lines-bt-${k.replace("|", "-")}`}><td className="px-2 py-1 text-slate-300">{l}</td>
                {["validation", "oos"].flatMap((sp) => TAGS.map(([t]) => <td key={sp + t} className="px-2 py-1 text-right text-slate-300">{cell(t, sp, k)}</td>))}</tr>
            ))}
            <tr className="border-t border-slate-800/60" data-testid="lines-bt-clv"><td className="px-2 py-1 text-slate-300">Avg CLV (priced at open)</td>
              {["validation", "oos"].flatMap((sp) => TAGS.map(([t]) => <td key={sp + t} className="px-2 py-1 text-right text-slate-400">{clv(t, sp)}</td>))}</tr>
            <tr className="border-t border-slate-800/60" data-testid="lines-bt-brier"><td className="px-2 py-1 text-slate-300">Brier / log loss (OOS) ML · PL · TOT</td>
              <td colSpan={4} className="px-2 py-1 text-right text-slate-400">Mar–Apr Brier/log-loss · {["moneyline", "puckline", "total"].map(br).join(" · ")}</td></tr>
          </tbody>
        </table>
      </div>
      {d.notes.map((n) => <p key={n} className="font-mono text-[9px] text-slate-600">• {n}</p>)}
    </div>
  );
};

const RecordTable = ({ t }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-800">
    <table className="w-full font-mono text-[11px]" data-testid="lines-audit-table">
      <thead className="bg-slate-900 text-[9px] uppercase tracking-wider text-slate-500">
        <tr>{["Group", "N", "W-L-P", "Hit %", "Risked", "Units", "ROI", "Avg CLV", "Avg edge"].map((h, i) => <th key={h} className={`px-2 py-1.5 ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {ROWS.filter(([k]) => t[k]).map(([k, label]) => {
          const r = t[k];
          return (
            <tr key={k} className="border-t border-slate-800/60" data-testid={`lines-audit-row-${k.replace(":", "-")}`}>
              <td className="px-2 py-1.5 text-slate-300">{label}</td>
              <td className="px-2 py-1.5 text-right text-slate-400">{r.n}</td>
              <td className="px-2 py-1.5 text-right text-slate-200">{r.w}-{r.l}-{r.p}</td>
              <td className="px-2 py-1.5 text-right text-slate-200">{r.rate ?? "—"}{r.rate != null && "%"}</td>
              <td className="px-2 py-1.5 text-right text-slate-400">{r.risked ?? r.n}u</td>
              <td className={`px-2 py-1.5 text-right font-bold ${r.units > 0 ? "text-emerald-300" : r.units < 0 ? "text-rose-400" : "text-slate-400"}`}>{signed(r.units)}u</td>
              <td className={`px-2 py-1.5 text-right ${r.roi > 0 ? "text-emerald-300" : r.roi < 0 ? "text-rose-400" : "text-slate-400"}`}>{signed(r.roi)}%</td>
              <td className="px-2 py-1.5 text-right text-slate-300">{r.avg_clv == null ? "—" : `${signed(r.avg_clv)} (${r.clv_n})`}</td>
              <td className="px-2 py-1.5 text-right text-slate-300">{r.avg_edge ?? "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const Recent = ({ rows }) => (
  <div className="max-h-72 overflow-auto rounded-lg border border-slate-800">
    <table className="w-full font-mono text-[10px]" data-testid="lines-audit-recent">
      <thead className="sticky top-0 bg-slate-900 text-[9px] uppercase tracking-wider text-slate-500">
        <tr>{["Date", "Game", "Pick", "Open", "Frozen", "Close", "Model", "Raw imp", "No-vig", "Fair", "Edge", "Exp ROI", "Status", "Audited", "Integrity", "Final", "Result", "CLV"].map((h) => <th key={h} className="whitespace-nowrap px-2 py-1.5 text-left">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((x, i) => (
          <tr key={`${x.date}-${x.event_id}-${x.market}`} className="border-t border-slate-800/60" data-testid={`lines-audit-pick-${i}`}>
            <td className="px-2 py-1 text-slate-500">{fmtDate(x.date)}</td>
            <td className="whitespace-nowrap px-2 py-1 text-slate-300">{x.game}</td>
            <td className="whitespace-nowrap px-2 py-1 text-slate-100">{x.label || x.selection} <span className="text-slate-600">{MKT[x.market]}</span>{x.best_bet && <span className="ml-1 rounded bg-amber-900/60 px-1 text-[8px] text-amber-300">BEST</span>}</td>
            <td className="px-2 py-1 text-slate-500">{odds(x.opening_odds)}</td>
            <td className="px-2 py-1 text-slate-300">{odds(x.odds)}</td>
            <td className="px-2 py-1 text-slate-500">{odds(x.closing_odds)}</td>
            <td className="px-2 py-1 text-slate-300">{x.model_pct ?? "—"}%</td>
            <td className="px-2 py-1 text-slate-500">{x.raw_implied_pct ?? "—"}</td>
            <td className="px-2 py-1 text-slate-500">{x.novig_pct ?? x.audited_novig_pct ?? "—"}</td>
            <td className="px-2 py-1 text-slate-500">{odds(x.fair)}</td>
            <td className="px-2 py-1 text-slate-300">{signed(x.edge)}</td>
            <td className="px-2 py-1 text-slate-300">{x.exp_roi != null ? `${signed(x.exp_roi)}%` : "—"}</td>
            <td className="px-2 py-1 text-slate-400">{x.original_status || x.status || "—"}</td>
            <td className="px-2 py-1 text-slate-400" title={[x.audited_reason, x.audit_basis].filter(Boolean).join(" · ")}>{x.audited_status || "—"}</td>
            <td className="px-2 py-1 text-slate-500">{x.integrity || "LEGACY"}</td>
            <td className="px-2 py-1 text-slate-400">{x.final || "—"}</td>
            <td className={`px-2 py-1 font-bold ${RES[x.result] || "text-slate-600"}`}>{x.result ? `${x.result} ${signed(x.units)}u` : "pending"}</td>
            <td className="px-2 py-1 text-slate-400">{x.clv == null ? "—" : signed(x.clv)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const LinesAudit = ({ lines, Panel, Calibration }) => {
  const t = lines?.totals || {};
  const all = t.all;
  return (
    <Panel title="Game lines audit · moneyline, puck line, totals" icon={DollarSign} testId="audit-lines">
      <p className="mb-2 font-mono text-[10px] text-slate-500">Every game's model selection is frozen at the pre-game price and graded 1 unit at those odds (final score incl. OT/SO). Only APPROVED rows are official bets; all other rows are hypothetical model tracking. CLV = closing no-vig % of the pick − no-vig % at the frozen price (pct pts, + = beat the close). {lines?.nights ? `${lines.nights} nights · model ${lines.model_version}` : ""}</p>
      {all ? <RecordTable t={t} /> : <p className="py-3 text-center font-mono text-[11px] text-slate-600" data-testid="lines-audit-empty">Game-line results appear after the first graded night with frozen lines.</p>}
      <Backtest />
      <div className="mt-3 grid grid-cols-1 gap-3">
        <div>
          <p className="mb-1 font-mono text-[9px] uppercase tracking-widest text-slate-500">Recent selections</p>
          {lines?.recent?.length ? <Recent rows={lines.recent} /> : <p className="py-3 font-mono text-[11px] text-slate-600">No frozen lines yet.</p>}
        </div>
        <div>
          <p className="mb-1 font-mono text-[9px] uppercase tracking-widest text-slate-500">Calibration · model win % vs actual</p>
          <Calibration data={lines?.calibration || []} />
        </div>
      </div>
    </Panel>
  );
};

export default LinesAudit;
